import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Paper,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Save,
  CheckCircle,
  Send,
  ArrowBack,
  Warning,
  Edit,
  Close
} from '@mui/icons-material';
import api from '../services/api';
import { getProperty } from '../services/propertyService';
import MarketAppraisalConfirmDialog from '../components/MarketAppraisalConfirmDialog';
import type { MarketAppraisalInputs } from '../types/marketAppraisal';
import { prepareAppraisalInputs } from '../utils/prepareAppraisalInputs';

const ConsultationReportEdit: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [reportContent, setReportContent] = useState('');
  const [reportStatus, setReportStatus] = useState<'draft' | 'ready' | 'finalized' | null>(null);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [sentAt, setSentAt] = useState<Date | null>(null);
  const [vendorEmail, setVendorEmail] = useState<string | null>(null);
  const [presentFindingsCompleted, setPresentFindingsCompleted] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [isEditing, setIsEditing] = useState(true);
  const [propertyOwnerEmail, setPropertyOwnerEmail] = useState<string | null>(null);
  const [showAppraisalDialog, setShowAppraisalDialog] = useState(false);
  const [appraisalInputs, setAppraisalInputs] = useState<MarketAppraisalInputs | null>(null);
  const [loadingAppraisalInputs, setLoadingAppraisalInputs] = useState(false);
  const [savedStructuredInputs, setSavedStructuredInputs] = useState<MarketAppraisalInputs | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    fetchReport();
    fetchPropertyOwnerEmail();
  }, [agencySlug, propertyId]);

  // Note: We don't auto-open the dialog on page load - user can click "Edit Inputs" when ready

  const fetchPropertyOwnerEmail = async () => {
    if (!agencySlug || !propertyId) return;
    try {
      const property = await getProperty(agencySlug, propertyId);
      if (property?.owner?.email) {
        setPropertyOwnerEmail(property.owner.email);
        // Pre-populate sendEmail with owner email if not already set
        if (!sendEmail && !vendorEmail) {
          setSendEmail(property.owner.email);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch property owner email:', err);
    }
  };

  const fetchReport = async () => {
    if (!agencySlug || !propertyId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report`);
      const report = response.data;
      
      // Check if we have content OR structuredInputs
      // If structuredInputs exist, we can still work with the report (user can edit inputs and generate PDF)
      if (report.content) {
        setReportContent(report.content);
      } else if (report.structuredInputs) {
        // We have structuredInputs but no content - this is OK, user can generate PDF from inputs
        setReportContent(''); // Empty content is fine, we'll generate from structuredInputs
        // Don't show error - user can still edit inputs and generate PDF
      } else {
        // No content and no structuredInputs - show error
        setError('Report content not found. The report may need to be regenerated.');
      }
      
      setReportStatus(report.status || 'draft');
      setReportUrl(report.url || null);
      setSentAt(report.sentAt ? new Date(report.sentAt) : null);
      setVendorEmail(report.vendorEmail || null);
      setPresentFindingsCompleted(report.presentFindingsCompleted || false);
      if (report.vendorEmail) {
        setSendEmail(report.vendorEmail);
      }
      // Store saved structured inputs if available
      if (report.structuredInputs) {
        setSavedStructuredInputs(report.structuredInputs);
      }
      // Disable editing if finalized, OR if both sent AND "Present Findings" is completed
      const isLocked = report.status === 'finalized' || (report.sentAt && report.presentFindingsCompleted);
      setIsEditing(!isLocked);
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load consultation report');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!agencySlug || !propertyId) return;
    
    setSaving(true);
    setError(null);
    try {
      // Include saved structured inputs if available
      const requestBody: any = { content: reportContent };
      if (savedStructuredInputs) {
        requestBody.structuredInputs = savedStructuredInputs;
      }
      
      const response = await api.put(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report`, requestBody);
      setSuccess('Report saved successfully. PDF preview has been updated.');
      setHasUnsavedChanges(false);
      // Update the PDF URL with the newly generated one
      if (response.data.url) {
        setReportUrl(response.data.url);
      }
      // If it was finalized, change status back to draft after editing
      if (reportStatus === 'finalized') {
        setReportStatus('draft');
        // Keep isEditing true since they're editing
      }
    } catch (err: any) {
      console.error('Error saving report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!agencySlug || !propertyId) return;
    
    if (!reportContent.trim()) {
      setError('Report content cannot be empty');
      return;
    }
    
    // Show dialog to ask if they want to send
    setShowFinalizeDialog(true);
  };
  

  const confirmFinalize = async (shouldSend: boolean) => {
    setShowFinalizeDialog(false);
    if (!agencySlug || !propertyId) return;
    
    setFinalizing(true);
    setError(null);
    try {
      const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/finalize-async`);
      setSuccess('Report finalization started. You will be notified when it completes.');
      
      // Don't update status immediately - wait for async completion
      // The notification will inform the user when it's done
      
      // If user wants to send, note that they'll need to wait for finalization
      if (shouldSend) {
        setSuccess('Report finalization started. Once finalized, you will be notified and can send it from here.');
      }
      
      // Close dialogs and redirect back to the property page
      setShowFinalizeDialog(false);
      navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
    } catch (err: any) {
      console.error('Error finalizing report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to finalize report');
    } finally {
      setFinalizing(false);
    }
  };

  const handleSend = async () => {
    if (!agencySlug || !propertyId) return;
    
    if (!sendEmail || !sendEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSending(true);
    setError(null);
    try {
      const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/send-email`, {
        email: sendEmail
      });
      setSuccess(`Report sent successfully to ${sendEmail}`);
      setShowSendDialog(false);
      setSentAt(new Date());
      setVendorEmail(sendEmail);
      // Refresh report data to get updated sentAt
      await fetchReport();
    } catch (err: any) {
      console.error('Error sending report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReportContent(e.target.value);
    setHasUnsavedChanges(true);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
      }
    } else {
      navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Only show error page if we have an error AND no content AND no structuredInputs
  // If structuredInputs exist, user can still edit and generate PDF
  if (error && !reportContent && !savedStructuredInputs) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Property
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Edit Consultation Report
            </Typography>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              variant="outlined"
            >
              Back to Property
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* AI Warning */}
          <Alert 
            severity="warning" 
            icon={<Warning />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" component="div">
              <strong>Important:</strong> This AI-generated report may contain inaccurate information. 
              Please review and modify the content for accuracy before finalizing and sending to the vendor.
            </Typography>
          </Alert>

          {/* Status indicator */}
          {reportStatus && (
            <Alert 
              severity={
                (sentAt && presentFindingsCompleted) 
                  ? 'warning' 
                  : reportStatus === 'finalized' 
                    ? 'success' 
                    : 'info'
              } 
              sx={{ mb: 3 }}
            >
              {(() => {
                let label: string;
                if (sentAt && presentFindingsCompleted) {
                  label = 'Sent & Presented (Locked)';
                } else if (isEditing && reportStatus === 'finalized') {
                  label = 'Draft (Editing)';
                } else if (reportStatus === 'finalized') {
                  label = 'Finalized';
                } else if (reportStatus === 'ready') {
                  label = 'Ready to Send';
                } else {
                  label = 'Draft';
                }
                return <>Report Status: <strong>{label}</strong></>;
              })()}

              {(sentAt && presentFindingsCompleted) && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This report has been sent to {vendorEmail || 'vendor'} on {new Date(sentAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} and the "Present Findings" task has been completed. The report is now locked from editing.
                </Typography>
              )}
              {reportStatus === 'finalized' && !isEditing && !sentAt && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This report has been finalized and is ready to send to the vendor.
                </Typography>
              )}
              {reportStatus === 'ready' && !sentAt && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  The PDF has been generated and the report is ready to send. You can still edit it, but you may wish to finalize again after major changes.
                </Typography>
              )}
              {isEditing && reportStatus === 'finalized' && !sentAt && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  You are editing a finalized report. Remember to finalize again before sending.
                </Typography>
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Input-focused interface with preview */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Market Appraisal Inputs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Edit the structured inputs below to customize your market appraisal report. The preview will update automatically based on your inputs.
            </Typography>
            
            {(sentAt && presentFindingsCompleted) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This report has been sent to the vendor on {new Date(sentAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} 
                  {vendorEmail ? ` (${vendorEmail})` : ''} and the "Present Findings" task has been completed. 
                  The report cannot be edited. If you need to make changes, please contact the vendor or create a new report.
                </Typography>
              </Alert>
            )}

            {savedStructuredInputs ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Structured inputs are saved. Click "Edit Inputs" below to modify them.
                  </Typography>
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={async () => {
                      setLoadingAppraisalInputs(true);
                      try {
                        const inputsResponse = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`);
                        const inputsData = inputsResponse.data;
                        
                        let initialInputs: MarketAppraisalInputs;
                        if (inputsData.savedStructuredInputs) {
                          initialInputs = inputsData.savedStructuredInputs;
                        } else {
                          initialInputs = prepareAppraisalInputs(
                            inputsData.property,
                            inputsData.agency,
                            inputsData.agent,
                            inputsData.extractedData,
                            { content: reportContent }
                          );
                        }
                        
                        setAppraisalInputs(initialInputs);
                        setShowAppraisalDialog(true);
                      } catch (err: any) {
                        console.error('Error loading appraisal inputs:', err);
                        setError('Failed to load inputs for editing.');
                      } finally {
                        setLoadingAppraisalInputs(false);
                      }
                    }}
                    disabled={loadingAppraisalInputs || (!!sentAt && presentFindingsCompleted)}
                  >
                    {loadingAppraisalInputs ? 'Loading...' : 'Edit Inputs'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  No structured inputs found. Please generate the report first or click "Edit Inputs" to create them.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={async () => {
                    setLoadingAppraisalInputs(true);
                    try {
                      const inputsResponse = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`);
                      const inputsData = inputsResponse.data;
                      
                      let initialInputs: MarketAppraisalInputs;
                      if (inputsData.savedStructuredInputs) {
                        initialInputs = inputsData.savedStructuredInputs;
                      } else {
                        initialInputs = prepareAppraisalInputs(
                          inputsData.property,
                          inputsData.agency,
                          inputsData.agent,
                          inputsData.extractedData,
                          { content: reportContent }
                        );
                      }
                      
                      setAppraisalInputs(initialInputs);
                      setShowAppraisalDialog(true);
                    } catch (err: any) {
                      console.error('Error loading appraisal inputs:', err);
                      setError('Failed to load inputs.');
                    } finally {
                      setLoadingAppraisalInputs(false);
                    }
                  }}
                  disabled={loadingAppraisalInputs}
                >
                  {loadingAppraisalInputs ? 'Loading...' : 'Edit Inputs'}
                </Button>
              </Alert>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {isEditing && !(sentAt && presentFindingsCompleted) && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={saving || finalizing || loadingAppraisalInputs || !hasUnsavedChanges}
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                  onClick={handleFinalize}
                  disabled={saving || finalizing || !reportContent.trim()}
                >
                  {finalizing ? 'Finalizing...' : 'Finalize Report'}
                </Button>
              </>
            )}
            
            {reportStatus === 'finalized' && !isEditing && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={async () => {
                  // Ensure we have the owner email before showing dialog
                  if (!propertyOwnerEmail) {
                    await fetchPropertyOwnerEmail();
                  }
                  // Pre-populate email if available
                  if (propertyOwnerEmail && !sendEmail && !vendorEmail) {
                    setSendEmail(propertyOwnerEmail);
                  }
                  setShowSendDialog(true);
                }}
                disabled={sending}
              >
                {sentAt ? 'Resend Report' : 'Send Report'}
              </Button>
            )}
          </Box>

          {/* Info about finalizing */}
          {!sentAt && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Once finalized, the report will be regenerated as a PDF and can be sent to the vendor. 
                You can still modify the report after finalizing, but you'll need to finalize again before sending.
              </Typography>
            </Box>
          )}

          {/* Preview links */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Preview Options:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {savedStructuredInputs && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    if (!agencySlug || !propertyId) return;
                    setPreviewModalOpen(true);
                    setPreviewLoading(true);
                    setPreviewError(null);
                    setPreviewHtml(null);
                    try {
                      const { data } = await api.post(
                        `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/preview`,
                        { structuredInputs: savedStructuredInputs },
                        { responseType: 'text', headers: { Accept: 'text/html', 'Content-Type': 'application/json' } }
                      );
                      setPreviewHtml(typeof data === 'string' ? data : '');
                    } catch (err: any) {
                      setPreviewError(err.response?.data?.error || err.message || 'Failed to load preview.');
                    } finally {
                      setPreviewLoading(false);
                    }
                  }}
                >
                  View Live HTML Preview
                </Button>
              )}
              {reportUrl && reportStatus === 'finalized' && (
                <Button
                  variant="outlined"
                  size="small"
                  href={reportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Finalized PDF
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Preview modal: HTML only, no app menu */}
      <Dialog
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewHtml(null);
          setPreviewError(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: 900 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Market Appraisal Preview</span>
          <IconButton
            aria-label="close preview"
            onClick={() => {
              setPreviewModalOpen(false);
              setPreviewHtml(null);
              setPreviewError(null);
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {previewLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 300 }}>
              <CircularProgress />
            </Box>
          )}
          {previewError && (
            <Alert severity="error" sx={{ m: 2 }} onClose={() => setPreviewError(null)}>
              {previewError}
            </Alert>
          )}
          {!previewLoading && previewHtml && (
            <iframe
              title="Market Appraisal Preview"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '100%', minHeight: 500, border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Finalize Confirmation Dialog */}
      <Dialog
        open={showFinalizeDialog}
        onClose={() => setShowFinalizeDialog(false)}
      >
        <DialogTitle>Finalize Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The report will be finalized and ready to send to the vendor. Would you like to send it now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => confirmFinalize(false)}>
            Finalize Only
          </Button>
          <Button onClick={() => confirmFinalize(true)} variant="contained" autoFocus>
            Finalize & Send
          </Button>
          <Button onClick={() => setShowFinalizeDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Market Appraisal Confirmation Dialog */}
      {appraisalInputs && (
        <MarketAppraisalConfirmDialog
          key={showAppraisalDialog ? 'open' : 'closed'} // Force re-initialization when dialog opens
          open={showAppraisalDialog}
          onClose={() => {
            setShowAppraisalDialog(false);
            setAppraisalInputs(null);
          }}
          onSave={async (confirmedInputs: MarketAppraisalInputs) => {
            try {
              // Save structured inputs with the report
              await api.put(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report`, {
                structuredInputs: confirmedInputs
              });
              
              setSavedStructuredInputs(confirmedInputs);
              setSuccess('Report inputs saved successfully.');
            } catch (err: any) {
              console.error('Error saving structured inputs:', err);
              setError('Failed to save structured inputs');
              throw err;
            }
          }}
          onConfirm={async (confirmedInputs: MarketAppraisalInputs) => {
            setShowAppraisalDialog(false);
            setFinalizing(true);
            
            try {
              // Generate PDF from structured inputs
              const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/generate-pdf`, {
                structuredInputs: confirmedInputs
              });
              
              setSavedStructuredInputs(confirmedInputs);
              if (response.data.url) {
                setReportUrl(response.data.url);
              }
              setIsEditing(true);
              setReportStatus('draft');
              setSuccess('PDF generated successfully. You can now edit the report content.');
            } catch (err: any) {
              console.error('Error generating PDF:', err);
              setError(err.response?.data?.error || 'Failed to generate PDF');
            } finally {
              setAppraisalInputs(null);
              setFinalizing(false);
            }
          }}
          initialInputs={appraisalInputs!}
          loading={loadingAppraisalInputs}
          agencySlug={agencySlug}
          propertyId={propertyId}
        />
      )}

      {/* Send Report Dialog */}
      <Dialog
        open={showSendDialog}
        onClose={() => !sending && setShowSendDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{sentAt ? 'Resend Report' : 'Send Report to Vendor'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {propertyOwnerEmail 
              ? `The report will be sent to the property owner's email address. You can change it if needed.`
              : 'Enter the vendor\'s email address to send the consultation report.'}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Vendor Email"
            type="email"
            fullWidth
            variant="outlined"
            value={sendEmail}
            onChange={(e) => setSendEmail(e.target.value)}
            disabled={sending}
            placeholder={propertyOwnerEmail || "vendor@example.com"}
            helperText={propertyOwnerEmail && sendEmail !== propertyOwnerEmail ? "Using a different email than the property owner" : undefined}
          />
          {sentAt && vendorEmail && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Previously sent to {vendorEmail} on {new Date(sentAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSendDialog(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} variant="contained" disabled={sending || !sendEmail}>
            {sending ? <CircularProgress size={24} /> : sentAt ? 'Resend' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConsultationReportEdit;

